/**
 * Alshallal Pastry Company HR System - Reset Password Page
 * Complete password reset with token
 */
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle, XCircle, KeyRound } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Password validation
  const [validation, setValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    match: false
  });

  useEffect(() => {
    setValidation({
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /\d/.test(newPassword),
      match: newPassword === confirmPassword && newPassword.length > 0
    });
  }, [newPassword, confirmPassword]);

  const isValid = Object.values(validation).every(Boolean);

  // No token - show error
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="p-4 bg-red-100 rounded-full w-fit mx-auto mb-6">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              {isRTL ? 'رابط غير صالح' : 'Invalid Link'}
            </h2>
            <p className="text-slate-600 mb-6">
              {isRTL 
                ? 'رابط إعادة تعيين كلمة المرور غير صالح أو منتهي الصلاحية.'
                : 'This password reset link is invalid or has expired.'
              }
            </p>
            <Link to="/forgot-password">
              <Button className="w-full">
                {isRTL ? 'طلب رابط جديد' : 'Request New Link'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/password-reset/confirm`, {
        token,
        new_password: newPassword
      });
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail?.error || t('error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="reset-success">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="p-4 bg-green-100 rounded-full w-fit mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              {isRTL ? 'تم إعادة تعيين كلمة المرور' : 'Password Reset Complete'}
            </h2>
            <p className="text-slate-600 mb-6">
              {isRTL 
                ? 'تم تغيير كلمة المرور بنجاح. يمكنك الآن تسجيل الدخول.'
                : 'Your password has been successfully changed. You can now log in.'
              }
            </p>
            <Button className="w-full" onClick={() => navigate('/login')}>
              {isRTL ? 'تسجيل الدخول' : 'Go to Login'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ValidationItem = ({ valid, label }) => (
    <div className={cn(
      "flex items-center gap-2 text-sm",
      valid ? "text-green-600" : "text-slate-400"
    )}>
      {valid ? <CheckCircle className="h-4 w-4" /> : <div className="h-4 w-4 rounded-full border-2 border-current" />}
      {label}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Language toggle */}
        <div className="flex justify-end mb-8">
          <Button variant="ghost" size="sm" onClick={toggleLanguage}>
            {language === 'en' ? 'العربية' : 'English'}
          </Button>
        </div>

        <Card>
          <CardHeader className="text-center">
            <div className="p-3 bg-slate-100 rounded-full w-fit mx-auto mb-4">
              <KeyRound className="h-8 w-8 text-slate-600" />
            </div>
            <CardTitle className="text-2xl">
              {isRTL ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
            </CardTitle>
            <CardDescription>
              {isRTL 
                ? 'أدخل كلمة مرور جديدة لحسابك'
                : 'Enter a new password for your account'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="newPassword">{t('new_password')}</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="h-11 pe-10"
                    data-testid="new-password-input"
                    dir="ltr"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={cn(
                      "absolute top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700",
                      isRTL ? "left-3" : "right-3"
                    )}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('confirm_password')}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="h-11"
                  data-testid="confirm-password-input"
                  dir="ltr"
                />
              </div>

              {/* Password requirements */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                <p className="text-sm font-medium text-slate-700 mb-2">
                  {isRTL ? 'متطلبات كلمة المرور:' : 'Password requirements:'}
                </p>
                <ValidationItem 
                  valid={validation.length} 
                  label={isRTL ? '8 أحرف على الأقل' : 'At least 8 characters'} 
                />
                <ValidationItem 
                  valid={validation.uppercase} 
                  label={isRTL ? 'حرف كبير واحد' : 'One uppercase letter'} 
                />
                <ValidationItem 
                  valid={validation.lowercase} 
                  label={isRTL ? 'حرف صغير واحد' : 'One lowercase letter'} 
                />
                <ValidationItem 
                  valid={validation.number} 
                  label={isRTL ? 'رقم واحد' : 'One number'} 
                />
                <ValidationItem 
                  valid={validation.match} 
                  label={isRTL ? 'كلمتا المرور متطابقتان' : 'Passwords match'} 
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading || !isValid}
                data-testid="reset-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                    {t('loading')}
                  </>
                ) : (
                  isRTL ? 'إعادة تعيين كلمة المرور' : 'Reset Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
